// Physics (cannon-es). Three.js draws, cannon-es simulates. Every piece exists
// twice: a Mesh (what you see) and a Body (what collides).
//
// Pieces standing on the board are STATIC bodies: immovable obstacles that the
// simulation never moves, but that flying pieces bounce off. When a piece is
// captured, knock() turns its body DYNAMIC, gives it a shove, and from then on
// the mesh follows the body every frame until it comes to rest.

import * as CANNON from 'cannon-es'
import * as THREE from 'three'
import { BOARD, PHYSICS, GAME_OVER } from '../config.js'

export function createPhysics() {
  const world = new CANNON.World({ gravity: new CANNON.Vec3(0, PHYSICS.gravity, 0) })
  world.broadphase = new CANNON.SAPBroadphase(world)   // faster collision search
  world.allowSleep = true                              // resting bodies stop costing CPU
  world.defaultContactMaterial.friction = PHYSICS.friction
  world.defaultContactMaterial.restitution = PHYSICS.restitution

  // Scenery. The board is a box whose top face is y = 0. The plate top, one
  // square-height lower, is modelled as an infinite floor so nothing falls forever.
  const half = (BOARD.size * BOARD.squareSize) / 2
  const board = new CANNON.Body({
    type: CANNON.Body.STATIC,
    shape: new CANNON.Box(new CANNON.Vec3(half, BOARD.squareHeight / 2, half)),
    position: new CANNON.Vec3(0, -BOARD.squareHeight / 2, 0)
  })
  const floor = new CANNON.Body({ type: CANNON.Body.STATIC, shape: new CANNON.Plane() })
  floor.quaternion.setFromEuler(-Math.PI / 2, 0, 0)   // Plane faces +z by default, turn it up
  floor.position.y = -BOARD.squareHeight
  world.addBody(board)
  world.addBody(floor)

  const entries = new Map()          // mesh -> { body, halfHeight, dynamic, strength, centred, toppled }
  const impactListeners = new Set()
  const restListeners = new Set()    // a knocked piece has come to rest off the board
  const up = new THREE.Vector3(0, 1, 0)
  const scratch = new THREE.Vector3()

  /** A tapered cylinder that roughly fills the piece's bounding box. */
  function shapeFor(mesh) {
    const box = mesh.geometry.boundingBox
    const radius = Math.max(box.max.x - box.min.x, box.max.z - box.min.z) / 2
    const height = box.max.y - box.min.y
    return { shape: new CANNON.Cylinder(radius * PHYSICS.topRadiusRatio, radius, height, 12), halfHeight: height / 2 }
  }

  /** Give a standing piece an immovable body. Call once per mesh. */
  function addPiece(mesh) {
    const { shape, halfHeight } = shapeFor(mesh)
    const body = new CANNON.Body({ type: CANNON.Body.STATIC, shape })
    const entry = { body, halfHeight, dynamic: false, strength: 1, centred: false, toppled: false }
    entries.set(mesh, entry)
    follow(mesh)
    world.addBody(body)
    reportImpacts(mesh, body)
    // A knocked piece that dozes off while still on the board gets one more shove.
    body.addEventListener('sleep', () => {
      if (!entry.dynamic || entry.toppled) return   // a fallen king lies where it fell
      const onBoard = Math.abs(body.position.x) < half + 0.4 && Math.abs(body.position.z) < half + 0.4 && body.position.y > -0.5
      if (onBoard) {
        body.wakeUp()
        const away = scratch.set(body.position.x, 0, body.position.z).normalize()
        shove(body, away, Math.max(0.7, entry.strength))   // a gentle knock still has to get it off the board
      } else {
        restListeners.forEach((fn) => fn(mesh))
      }
    })
  }

  /**
   * Set a body's velocity so it lands `knockReach` past the nearest board edge:
   * the lift fixes the flight time, the distance to cover fixes the speed. So a
   * piece in the middle of the board gets a harder shove than one at the edge,
   * and every strength clears the board.
   */
  function shove(body, direction, strength) {
    const lift = PHYSICS.knockLift * (0.6 + 0.4 * strength)
    const flightTime = (2 * lift) / -PHYSICS.gravity
    const reach = PHYSICS.knockReach.min + (PHYSICS.knockReach.max - PHYSICS.knockReach.min) * strength
    const { x, z } = body.position
    const outwardX = Math.abs(direction.x) >= Math.abs(direction.z)
    const toEdge = half - (outwardX ? Math.abs(x) : Math.abs(z))
    const along = Math.max(0.5, outwardX ? Math.abs(direction.x) : Math.abs(direction.z))   // how much of the shove points outward
    const speed = ((Math.max(0, toEdge) + reach) / along / flightTime) * PHYSICS.knockSpeedFactor
    body.velocity.set(direction.x * speed, lift, direction.z * speed)
    return lift
  }

  function reportImpacts(mesh, body) {
    body.addEventListener('collide', (event) => {
      const speed = Math.abs(event.contact.getImpactVelocityAlongNormal())
      impactListeners.forEach((fn) => fn(mesh, speed))
    })
  }

  /**
   * A shard of a shattered piece: a dynamic box the size of the fragment,
   * already moving. The mesh's origin is its centre, so mesh and body coincide.
   */
  function addFragment(mesh, size, velocity, angularVelocity) {
    const shape = new CANNON.Box(new CANNON.Vec3(Math.max(0.03, size.x / 2), Math.max(0.03, size.y / 2), Math.max(0.03, size.z / 2)))
    const body = new CANNON.Body({
      mass: Math.max(0.05, size.x * size.y * size.z * 2),
      shape,
      position: new CANNON.Vec3(mesh.position.x, mesh.position.y, mesh.position.z)
    })
    body.quaternion.set(mesh.quaternion.x, mesh.quaternion.y, mesh.quaternion.z, mesh.quaternion.w)
    body.velocity.set(velocity.x, velocity.y, velocity.z)
    body.angularVelocity.set(angularVelocity.x, angularVelocity.y, angularVelocity.z)
    body.linearDamping = PHYSICS.knockDamping.linear
    body.angularDamping = PHYSICS.knockDamping.angular
    world.addBody(body)
    entries.set(mesh, { body, halfHeight: 0, dynamic: true, strength: 1, centred: true })
    reportImpacts(mesh, body)
  }

  /** Sink a standing piece's body far below the board (the piece shattered). restore() brings it back. */
  function park(mesh) {
    const entry = entries.get(mesh)
    if (!entry || entry.dynamic) return
    entry.body.position.y = -50
  }

  /** Move a STATIC body to wherever its mesh now is (after a move animation, or laid on its side by a restore). */
  function follow(mesh) {
    const entry = entries.get(mesh)
    if (!entry || entry.dynamic) return
    scratch.set(0, entry.halfHeight, 0).applyQuaternion(mesh.quaternion)   // mesh origin is the base, body origin the centre
    entry.body.position.set(mesh.position.x + scratch.x, mesh.position.y + scratch.y, mesh.position.z + scratch.z)
    entry.body.quaternion.copy(mesh.quaternion)
  }

  /**
   * Turn a piece loose: dynamic, shoved off the board. The shove points at the
   * nearest board edge (shortest way out, fewest pieces in the way), bent a
   * little towards `travel`, the capturer's line of approach, so it reads as
   * "knocked" rather than "ejected". `strength` 0..1 (the Settings slider)
   * sets how far past the edge it lands, how high it arcs and how fast it spins.
   */
  function knock(mesh, travel, strength = 1) {
    const entry = entries.get(mesh)
    if (!entry) return
    follow(mesh)
    const { body } = entry
    entry.strength = strength
    const spin = PHYSICS.knockSpin * strength
    const { x, z } = mesh.position
    const outward = Math.abs(x) >= Math.abs(z) ? new THREE.Vector3(Math.sign(x) || 1, 0, 0) : new THREE.Vector3(0, 0, Math.sign(z) || 1)
    const direction = outward.addScaledVector(travel, PHYSICS.travelWeight).normalize()
    body.type = CANNON.Body.DYNAMIC
    body.mass = 1
    body.updateMassProperties()
    body.linearDamping = PHYSICS.knockDamping.linear
    body.angularDamping = PHYSICS.knockDamping.angular
    shove(body, direction, strength)
    const axis = scratch.copy(up).cross(direction).normalize()   // tumble end over end
    body.angularVelocity.set(axis.x * spin, (Math.random() - 0.5) * 2 * strength, axis.z * spin)
    body.wakeUp()
    entry.dynamic = true
  }

  /**
   * Tip a standing piece over where it stands (the loser's king): dynamic, then
   * a push at its top along `direction`, the way a finger would, so it falls
   * on its side and stays on the board.
   */
  function topple(mesh, direction, push = GAME_OVER.topplePush) {
    const entry = entries.get(mesh)
    if (!entry) return
    follow(mesh)
    const { body, halfHeight } = entry
    body.type = CANNON.Body.DYNAMIC
    body.mass = 1
    body.updateMassProperties()
    body.linearDamping = 0.2
    body.angularDamping = 0.2
    const top = new CANNON.Vec3(body.position.x, body.position.y + halfHeight, body.position.z)
    body.applyImpulse(new CANNON.Vec3(direction.x * push, 0, direction.z * push), top)
    body.wakeUp()
    entry.dynamic = true
    entry.toppled = true
  }

  /** A knocked or toppled piece is put back into play: static again, following its mesh. */
  function restore(mesh) {
    const entry = entries.get(mesh)
    if (!entry) return
    const { body } = entry
    body.type = CANNON.Body.STATIC
    body.mass = 0
    body.updateMassProperties()
    body.velocity.setZero()
    body.angularVelocity.setZero()
    entry.dynamic = false
    entry.toppled = false
    follow(mesh)
  }

  /** Replace the collider (after a promotion changes the geometry). */
  function reshape(mesh) {
    remove(mesh)
    addPiece(mesh)
  }

  function remove(mesh) {
    const entry = entries.get(mesh)
    if (!entry) return
    world.removeBody(entry.body)
    entries.delete(mesh)
  }

  function clear() {
    for (const mesh of [...entries.keys()]) remove(mesh)
  }

  /** Advance the simulation and copy every dynamic body onto its mesh. */
  function step(dt) {
    world.step(1 / 60, dt, 3)   // fixed 60 Hz steps, at most 3 per frame
    for (const [mesh, { body, halfHeight, dynamic, centred }] of entries) {
      if (!dynamic) continue
      mesh.quaternion.copy(body.quaternion)
      if (centred) { mesh.position.copy(body.position); continue }   // shards: origin at the middle
      // body.position is the centre of the cylinder, mesh.position is its base
      scratch.set(0, halfHeight, 0).applyQuaternion(mesh.quaternion)
      mesh.position.copy(body.position).sub(scratch)
    }
  }

  function onImpact(fn) {
    impactListeners.add(fn)
    return () => impactListeners.delete(fn)
  }

  /** fn(mesh) when a knocked piece settles off the board. */
  function onRest(fn) {
    restListeners.add(fn)
    return () => restListeners.delete(fn)
  }

  return { world, entries, addPiece, addFragment, park, follow, knock, topple, restore, reshape, remove, clear, step, onImpact, onRest }
}
